import { graphQlClient } from '@config/graphqlClient';
import { SubscriptionPlanGraphQL } from '@graphql/subscriptionPlan.queries';
import { SubscriptionPlanTemplate, PlanAddonSummary } from '@models/subscription';

export interface CreateSubscriptionPlanPayload {
  name: string;
  payMethod?: string;
  basePriceCents: number;
  includedSeats: number;
  isPublic: boolean;
  isAddOn: boolean;
  trialDurationDays?: number;
  requiresPaymentUpfront?: boolean;
  validFrom?: string;
  validTo?: string;
}

export interface UpdateSubscriptionPlanPayload {
  name?: string;
  payMethod?: string;
  basePriceCents?: number;
  includedSeats?: number;
  isPublic?: boolean;
  isAddOn?: boolean;
  trialDurationDays?: number;
  requiresPaymentUpfront?: boolean;
  validFrom?: string;
  validTo?: string;
}

export const subscriptionPlanService = {
  /**
   * Get all subscription plan templates (Super Admin only - includes add-ons)
   */
  async getAll(): Promise<SubscriptionPlanTemplate[]> {
    const data: any = await graphQlClient.request(
      SubscriptionPlanGraphQL.queries.GET_ALL_SUBSCRIPTION_PLANS,
    );
    return data.getAllSubscriptionPlans;
  },

  /**
   * Get subscription plan by ID (Super Admin only)
   */
  async getById(planId: string): Promise<SubscriptionPlanTemplate> {
    if (!planId) {
      throw new Error('Plan ID is required');
    }

    const data: any = await graphQlClient.request(
      SubscriptionPlanGraphQL.queries.GET_SUBSCRIPTION_PLAN_BY_ID,
      { planId },
    );
    return data.getSubscriptionPlanById;
  },

  /**
   * Create subscription plan (Super Admin only)
   */
  async create(payload: CreateSubscriptionPlanPayload): Promise<SubscriptionPlanTemplate> {
    if (!payload.name || payload.basePriceCents === undefined) {
      throw new Error('Name and base price are required');
    }

    const data: any = await graphQlClient.request(
      SubscriptionPlanGraphQL.mutations.CREATE_SUBSCRIPTION_PLAN,
      { input: payload },
    );
    return data.createSubscriptionPlan;
  },

  /**
   * Update subscription plan (Super Admin only)
   */
  async update(
    planId: string,
    payload: UpdateSubscriptionPlanPayload,
  ): Promise<SubscriptionPlanTemplate> {
    if (!planId) {
      throw new Error('Plan ID is required');
    }

    const data: any = await graphQlClient.request(
      SubscriptionPlanGraphQL.mutations.UPDATE_SUBSCRIPTION_PLAN,
      { planId, input: payload },
    );
    return data.updateSubscriptionPlan;
  },

  /**
   * Delete subscription plan (Super Admin only - Soft delete)
   */
  async delete(planId: string): Promise<boolean> {
    if (!planId) {
      throw new Error('Plan ID is required');
    }

    const data: any = await graphQlClient.request(
      SubscriptionPlanGraphQL.mutations.DELETE_SUBSCRIPTION_PLAN,
      { planId },
    );
    return data.deleteSubscriptionPlan;
  },

  /**
   * Sprint 17, story-082 — SUPER_ADMIN only.
   */
  async linkAddonToPlan(planId: string, addonId: string, priceCents: number): Promise<PlanAddonSummary> {
    const data: any = await graphQlClient.request(SubscriptionPlanGraphQL.mutations.LINK_ADDON_TO_PLAN, {
      planId,
      addonId,
      priceCents,
    });
    return data.linkAddonToPlan;
  },

  async unlinkAddonFromPlan(planId: string, addonId: string): Promise<boolean> {
    const data: any = await graphQlClient.request(SubscriptionPlanGraphQL.mutations.UNLINK_ADDON_FROM_PLAN, {
      planId,
      addonId,
    });
    return data.unlinkAddonFromPlan;
  },

  async repriceAddonLink(planId: string, addonId: string, priceCents: number): Promise<PlanAddonSummary> {
    const data: any = await graphQlClient.request(SubscriptionPlanGraphQL.mutations.REPRICE_ADDON_LINK, {
      planId,
      addonId,
      priceCents,
    });
    return data.repriceAddonLink;
  },
};

export default subscriptionPlanService;
